'use client'

import {animate} from 'motion'
import Script from 'next/script'
import {useEffect, useRef, useState} from 'react'
import {cx} from '@local/utils/cx'
import {Form, Modal, Button, InputSubmit} from '@local/ui/components'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import CrossSvg from '@local/assets/svgs/icon-close.svg'
import {expoIn, expoOut} from 'eases'
import {useIsomorphicLayoutEffect, useSession} from '@local/hooks'
import {usePathname} from 'next/navigation'

export interface ComingSoonSignupProps {
	className?: string,
	newsletter:{
		mailchimpId: string,
		typeformID: string,
		surtitle: string,
		successMessage: string,
	}
}

const NEWSLETTER_POPUP_SHOWN = 'notShown'

export const ComingSoonSignup = ({className, newsletter}: ComingSoonSignupProps) => {
	const innerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)
	const [errorText, setErrorText] = useState<string>('')
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
	const [newsletterPopUpShown, setNewsletterPopUpShown] = useSession<string>('newsletterPopUpShown', NEWSLETTER_POPUP_SHOWN)
	const pathname = usePathname()

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/newsletter/subscribe', {
				method: 'POST',
				body: JSON.stringify({email_address: body.email, listID: newsletter.mailchimpId}),
			})
			const result = await res.json()

			if (res.status === 200 && result.message === 'success') {
				setIsSuccess(true)
				setIsLoading(false)
				setIsModalOpen(true)
				closeNewsletterPopUp(4)
				return {status: 200}
			} else {
				setIsLoading(false)
				setErrorText(result.message)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				setErrorText('Enter a valid email.')
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}
			if (error.message === 'RequestFailed') {
				setErrorText('Something went wrong.')
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	useEffect(() => {
		if (isSuccess) {
			setTimeout(() => {
				setIsSuccess(false)
			}, 5000)
		}
	}, [isSuccess])

	const handleClose = () => setIsModalOpen(false)

	const handleAdditionalReset = () => {
		inputRef.current?.blur()
	}

	useIsomorphicLayoutEffect(() => {
		if (pathname !== '/' && newsletterPopUpShown === 'notShown' && innerRef?.current) {
			innerRef.current.style.transform = 'translate3d(0,150%,0)'
			innerRef.current.style.visibility = 'hidden'
			animate(
				innerRef.current,
				{transform: 'translate3d(0,0,0)', visibility: 'visible'},
				{duration: 0.66, delay: 10, easing: expoOut},
			)
		}
	}, [])

	// typeform script
	useEffect(() => {
		// @ts-expect-error access `window.tf` global namepsace injected from Typeform
		window.tf?.load()

		let timeout

		// @ts-expect-error access `window.handleTypeformSubmit` global namepsace injected from Typeform
		window.handleTypeformSubmit = () => {
			timeout = setTimeout(() => {
				setIsModalOpen(false)
			}, 4000)
		}

		return () => {
			clearTimeout(timeout)
		}
	}, [])

	const closeNewsletterPopUp = (delay) => {
		if (innerRef?.current) {
			animate(
				innerRef.current,
				{transform: 'translate3d(0,150%,0)', visibility: 'hidden'},
				{duration: 0.66, delay, easing: expoIn},
			)
		}
		setNewsletterPopUpShown('shown')
	}

	if (!newsletter?.mailchimpId || pathname === '/') return <></>

	return (
		<>
			<div
				ref={innerRef}
				className={cx(
					'bg-grey-1 rounded text-grey-5 z-1',
					'fixed margin-left-1 span-w-12 md:span-w-3-wider lg:span-w-3-wide gutter-bottom-1',
					'transform-gpu invisible translate-y-[150%]',
					pathname.startsWith('/experience') && 'lg-max:!bottom-72',

				)}>
				<Form
					autoReset
					additionalReset={handleAdditionalReset}
					// @ts-expect-error handleSubmit can't decide between Promis and void
					onSubmit={handleSubmit}
					errorClassName='absolute bottom-6 lg:bottom-9 left-20 bg-grey-1 w-fit px-3'
					successMessage={newsletter.successMessage}
					successClassName='text-grey-kaerText px-14 pt-16 pb-24 bottom-8 h-66 bottom-0 !mt-0 z-2 bg-grey-bright text-12 tracking-widest flex items-center'
					className={cx(
						'flex flex-col gap-10 lg:gap-12 px-10 lg:px-14 py-12 lg:py-14',
						className,
					)}>
					<div className='flex justify-between h-fit items-center'>
						<p className='text-10 leading-none uppercase font-medium tracking-widest text-grey-kaerText-alpha-50'>{newsletter.surtitle}</p>
						<Button
							type='button'
							onClick={() => closeNewsletterPopUp(0)} className={cx(
								'text-black w-16 h-16 [&_svg]:w-full [&_svg]:h-full p-4',
							)}>
							<CrossSvg />
						</Button>
					</div>
					<InputSubmit
						type='name'
						name='email'
						label='Email Address'
						aria-autocomplete='none'
						autoComplete='off'
						inputRef={inputRef}
						onInvalid={() => setHasError(true)}
						pattern={EMAIL_PATTERN}
						theme='light'
						hasError={hasError}
						errorMessage={hasError ? errorText : ''}
						isLoading={isLoading}
						isSuccess={isSuccess}
						errorClassName='z-2 !bg-grey-1'
						submitClassName='!bg-grey-1'
						inputClassName='h-44 lg:h-48 text-12 tracking-[0.096rem] rounded-[0.25rem] pl-16 pr-34'
						labelClassName='text-12 uppercase tracking-[0.096rem] peer-focus:text-8 !bg-grey-1'
						className={cx(isSuccess && 'opacity-0', 'transition-opacity ease-out')} />
					<input type='submit' hidden />
				</Form>
			</div>
			{/* modal */}
			{newsletter.typeformID && (
				<Modal
					isActive={isModalOpen}
					onClickClose={handleClose}
					className='lg-max:h-3/4'>
					<Button
						onClick={handleClose} className={cx(
							'absolute top-16 right-16 text-black w-24 h-24 [&_svg]:w-full [&_svg]:h-full p-4 z-20',
						)}>
						<CrossSvg />
					</Button>
					<div
						className='z-10 h-full'
						data-tf-widget={newsletter.typeformID}
						data-tf-opacity='100'
						data-tf-inline-on-mobile
						data-tf-iframe-props='title=Kaer'
						data-tf-transitive-search-params
						data-tf-auto-focus
						data-tf-hide-footer
						data-tf-lazy
						data-tf-on-submit='handleTypeformSubmit'
						data-tf-medium='snippet' />
					<Script src='//embed.typeform.com/next/embed.js' strategy='lazyOnload' />
				</Modal>
			)}
		</>
	)
}
